import { getRuleReferencedEntityIds } from "@common/helpers/ruleEntities";

interface ResolveHistoryEntityIdsInput {
    entityIds: number[];
    resources?: Array<Record<string, unknown>>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toEntityId(value: unknown): number | null {
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value === "string" && value.length > 0) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

function extractIdsFromConditionValue(value: unknown): number[] {
    if (value === null || value === undefined) {
        return [];
    }

    if (Array.isArray(value)) {
        return value.flatMap((item) => extractIdsFromConditionValue(item));
    }

    if (typeof value === "object") {
        const record = value as Record<string, unknown>;
        if ("id" in record) {
            const entityId = toEntityId(record.id);
            return entityId === null ? [] : [entityId];
        }
        return [];
    }

    const entityId = toEntityId(value);
    return entityId === null ? [] : [entityId];
}

function extractEntityIdsFromResource(
    resource: Record<string, unknown>,
): number[] {
    const collected: number[] = [];

    const directEntityId = toEntityId(resource.entityId);
    if (directEntityId !== null) {
        collected.push(directEntityId);
    }

    const nestedEntity = resource.entity as { id?: unknown } | undefined;
    const nestedEntityId = nestedEntity ? toEntityId(nestedEntity.id) : null;
    if (nestedEntityId !== null) {
        collected.push(nestedEntityId);
    }

    for (const [key, value] of Object.entries(resource)) {
        if (!key.startsWith("condition:")) {
            continue;
        }
        if (!value || typeof value !== "object") {
            continue;
        }
        const conditionSnapshot = value as { value?: unknown };
        collected.push(
            ...extractIdsFromConditionValue(conditionSnapshot.value),
        );
    }

    if (Array.isArray(resource.conditions) || Array.isArray(resource.actions)) {
        collected.push(
            ...getRuleReferencedEntityIds({
                conditions: Array.isArray(resource.conditions)
                    ? resource.conditions
                    : undefined,
                actions: Array.isArray(resource.actions)
                    ? resource.actions
                    : undefined,
            }),
        );
    }

    const metadata = resource.metadata;
    if (isRecord(metadata) && Array.isArray(metadata.entityIds)) {
        for (const id of metadata.entityIds) {
            const entityId = toEntityId(id);
            if (entityId !== null) {
                collected.push(entityId);
            }
        }
    }

    return collected;
}

export function resolveHistoryEntityIds({
    entityIds,
    resources,
}: ResolveHistoryEntityIdsInput): number[] {
    if (entityIds.length > 0) {
        return entityIds;
    }

    const extracted = (resources ?? []).flatMap((resource) =>
        extractEntityIdsFromResource(resource),
    );

    return Array.from(new Set(extracted));
}
